// Types shared between the frontend (src) and the API (functions).

export type BudgetStyle = "shoestring" | "value" | "comfort" | "luxury";
export type Pace = "relaxed" | "balanced" | "packed";
export type EnergyLevel = "low" | "moderate" | "high";
export type AgeBand = "infant" | "child" | "teen" | "adult" | "senior";
export type Relation = "me" | "partner" | "family" | "friend" | "colleague";

// Trip objectives (group-level "why are we going").
export const OBJECTIVES = [
  "relaxation",
  "adventure",
  "culture",
  "food",
  "nature",
  "nightlife",
  "shopping",
  "family-time",
  "romance",
  "wellness",
  "history",
  "photography",
  "business",
] as const;
export type Objective = (typeof OBJECTIVES)[number];

export interface Traveler {
  id?: string;
  name: string;
  ageBand?: AgeBand;
  age?: number | null;
  relation?: Relation;
  interests: string[];
  dietary: string[];
  accessibility: string[];
  energyLevel?: EnergyLevel;
  notes?: string;
}

export interface GroupPreferences {
  pace: Pace;
  mustDo: string[];
  mustAvoid: string[];
  dietaryGroup: string[]; // constraints that apply to the whole party
  notes?: string;
}

// A single stop on a multi-city route. `nights` = how many days to spend there
// (a stop can repeat, e.g. returning to the arrival city at the end).
export interface TripStop {
  city: string;
  nights?: number | null;
}

export interface Trip {
  id?: string;
  slug?: string;
  title: string;
  origin?: string;
  destination: string; // overall region / trip label, e.g. "Yunnan, China"
  stops?: TripStop[]; // ordered multi-city route; empty/absent = single-city trip
  startDate?: string;
  endDate?: string;
  budgetTotal?: number | null;
  budgetCurrency: string;
  budgetStyle?: BudgetStyle;
  objectives: Objective[];
  groupPrefs: GroupPreferences;
  travelers: Traveler[];
  ownerEmail?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ---- Itinerary shape produced by the AI adapter ----

export interface CostEstimate {
  amount: number;
  currency: string;
  per: "person" | "group";
}

export interface SearchLink {
  label: string;
  url: string;
}

export type BlockCategory =
  | "food"
  | "sight"
  | "activity"
  | "transit"
  | "rest"
  | "accommodation"
  | "shopping"
  | "nightlife";

export interface ItineraryBlock {
  time: "morning" | "afternoon" | "evening";
  title: string;
  description: string;
  category: BlockCategory;
  estCost?: CostEstimate | null;
  bookingHint?: string;
  // Which travelers/objectives this satisfies, e.g. "Alice: art", "objective: food".
  matchesPreferences?: string[];
  searchLinks?: SearchLink[];
}

export interface ItineraryDay {
  date?: string;
  dayNumber: number;
  title: string;
  location?: string; // which city this day is based in (multi-city trips)
  summary?: string;
  blocks: ItineraryBlock[];
  dailyCostEstimate?: CostEstimate | null;
}

export interface BudgetBreakdown {
  currency: string;
  lodging?: number;
  food?: number;
  activities?: number;
  transport?: number;
  misc?: number;
  totalEstimate?: number;
  perPersonEstimate?: number;
  withinBudget?: boolean | null;
  note?: string;
}

export interface Itinerary {
  summary: string;
  days: ItineraryDay[];
  budget: BudgetBreakdown;
  conflictsFlagged: string[]; // how competing individual/group prefs were balanced
  packingTips: string[];
  localTips: string[];
}

export interface ItineraryRecord {
  id: string;
  version: number;
  content: Itinerary;
  modelUsed: string;
  isFinal: boolean;
  createdAt: string;
}

// API payloads
export interface TripWithItinerary extends Trip {
  itinerary?: ItineraryRecord | null;
  itineraryVersions?: { version: number; createdAt: string; isFinal: boolean }[];
}
